import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { UserAvatar } from "./UserAvatar";
import { Mail, Phone, Cake, Briefcase, Loader2 } from "lucide-react";

export const MONTHS_IT = ["", "gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno",
  "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];

const ROLE_LABEL = {
  super_admin: "Super Admin",
  admin: "Amministratore",
  operator: "Operatore",
};

const InfoRow = ({ icon: Icon, children, href, testId }) => (
  <div className="flex items-center gap-2.5 text-sm text-text-secondary" data-testid={testId}>
    <Icon size={14} strokeWidth={1.6} className="text-text-muted shrink-0" />
    {href ? (
      <a href={href} className="hover:underline text-text-primary truncate">{children}</a>
    ) : (
      <span className="truncate">{children}</span>
    )}
  </div>
);

/**
 * Scheda utente (avatar, nome, mansione, contatti, compleanno).
 * Visibile solo dentro il tenant (il backend applica l'isolamento org).
 */
export const UserCardDialog = ({ userId, onClose }) => {
  const [card, setCard] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!userId) return;
    setCard(null);
    setError(false);
    api.get(`/users/${userId}/card`)
      .then(({ data }) => setCard(data))
      .catch(() => setError(true));
  }, [userId]);

  if (!userId) return null;

  const birthday = card?.birthday_day
    ? `${card.birthday_day} ${MONTHS_IT[card.birthday_month]}${card.birthday_year ? ` ${card.birthday_year}` : ""}`
    : null;

  return (
    <Dialog open={!!userId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-xl bg-surface sm:max-w-sm" data-testid="user-card-dialog">
        <DialogHeader>
          <DialogTitle className="sr-only">Scheda utente</DialogTitle>
        </DialogHeader>
        {error ? (
          <div className="text-sm text-text-muted py-6 text-center">Scheda non disponibile.</div>
        ) : !card ? (
          <div className="py-8 text-center text-text-muted">
            <Loader2 size={18} className="animate-spin mx-auto" />
          </div>
        ) : (
          <div className="flex flex-col items-center text-center pt-2">
            <UserAvatar userId={card.id} name={card.full_name || card.email} version={card.avatar_version} size={72} testId="user-card-avatar" />
            <div className="mt-3 text-lg font-semibold text-text-primary" data-testid="user-card-name">
              {card.full_name || card.email}
            </div>
            {card.job_title && (
              <div className="text-sm text-text-secondary mt-0.5" data-testid="user-card-job-title">{card.job_title}</div>
            )}
            <div className="flex items-center gap-2 mt-2 flex-wrap justify-center">
              <span className="wl-badge wl-badge--neutral">{ROLE_LABEL[card.role] || card.role}</span>
              {card.attendance_exempt && (
                <span className="wl-badge wl-badge--info" data-testid="user-card-exempt-badge">Collaboratore esterno</span>
              )}
            </div>
            <div className="w-full mt-5 pt-4 border-t border-border space-y-2.5 text-left">
              <InfoRow icon={Mail} href={`mailto:${card.email}`} testId="user-card-email">{card.email}</InfoRow>
              {card.phone && (
                <InfoRow icon={Phone} href={`tel:${card.phone.replace(/\s+/g, "")}`} testId="user-card-phone">{card.phone}</InfoRow>
              )}
              {!card.job_title && card.role && (
                <InfoRow icon={Briefcase}>{ROLE_LABEL[card.role] || card.role}</InfoRow>
              )}
              {birthday && (
                <InfoRow icon={Cake} testId="user-card-birthday">Compleanno: {birthday}</InfoRow>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
